// Third Party
import * as React from 'react';
import ReactDOM from 'react-dom';
import { Transition, TransitionChild } from '@headlessui/react';
import {
  DayPickerRangeController,
  DayPickerRangeControllerShape,
  FocusedInputShape,
} from 'react-dates';
import moment from 'moment';

// Utils
import { useMounted } from '@hooks';
import { isSSR } from '@utils/predicates';
import { useQuotes } from './quotes-context';
import { formatDateYYYYMMDD, formatDateMMMD } from '@utils/date-helpers';
import { ListingBySlugPartial } from './utils';

// UI
import { Button } from '@components/button';
import { ButtonIcon } from '@components/button-icon';
import { IconNames } from '@components/icon';
import { Input } from '@components/form/input';
import { Typography } from '@components/typography';
import { QuoteBreakdownInfo } from './quote-breakdown-info';

type Props = {
  isOpen: boolean;
  closeModal: VoidFunction;
  listing: ListingBySlugPartial;
};

interface IHandleDatesChange {
  startDate: moment.Moment | null;
  endDate: moment.Moment | null;
}

// Cast DayPickerRangeController to ComponentType to resolve React JSX type conflicts
// This tells TypeScript to treat it as a valid React component that can accept props
const DayPickerComponent =
  DayPickerRangeController as React.ComponentType<DayPickerRangeControllerShape>;

export const QuotesModal: React.FC<Props> = ({
  isOpen,
  closeModal,
  listing,
}) => {
  const mounted = useMounted();
  const [dateRangePickerFocusedInput, setDateRangePickerFocusedInput] =
    React.useState<FocusedInputShape | null>('startDate');

  // This is cached so that it does not cause a re-render
  // in the date range picker component
  const currentMoment = React.useMemo(moment, []);

  const {
    availabilityDataLoading,
    checkInDate,
    setCheckInDate,
    checkOutDate,
    setCheckOutDate,
    isDayBlocked,
    isStartDayBlocked,
    isEndDayBlocked,
    minimumNightsForSelectedDate,
    getMinNightsForHoverDate,
    datesInvalid,
    bookingQuotePresent,
    datesPresent,
  } = useQuotes();

  const dateRangeText = () => {
    if (checkInDate && checkOutDate) {
      return `${moment(checkOutDate).diff(moment(checkInDate), 'days')} nights`;
    } else if (!checkInDate) {
      return 'Select check-in date';
    } else if (checkInDate && !checkOutDate) {
      return 'Select check-out date';
    } else {
      return 'Select dates for pricing';
    }
  };

  const dateRangeDetailsText = () => {
    if (checkInDate && checkOutDate) {
      return `${checkInDate} - ${checkOutDate}`;
    } else {
      return '';
    }
  };

  const onDatesChange = React.useCallback(
    ({ startDate, endDate }: IHandleDatesChange) => {
      setCheckInDate(startDate ? formatDateYYYYMMDD(startDate) : null);
      setCheckOutDate(endDate ? formatDateYYYYMMDD(endDate) : null);
    },
    []
  );

  const onClearDatesClick = React.useCallback(() => {
    setCheckInDate(null);
    setCheckOutDate(null);
    setDateRangePickerFocusedInput('startDate');
  }, []);

  const handleEscapeKeyPress = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      closeModal();
    }
  };

  React.useEffect(() => {
    if (mounted) {
      document.body.classList.add('modalActive');
      document.addEventListener('keyup', handleEscapeKeyPress);
    } else {
      document.body.classList.remove('modalActive');
      document.removeEventListener('keyup', handleEscapeKeyPress);
    }
  }, [mounted]);

  if (!mounted) return null;

  if (!isSSR) {
    return ReactDOM.createPortal(
      <Transition
        show={isOpen}
        as="div"
        className="absolute sm:hidden inset-0 bottom-[73px] w-full h-screen"
        style={{ zIndex: 1000 }}
      >
        <div className="block items-end justify-center text-center overflow-y-auto h-full">
          <TransitionChild
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed bottom-[73px] inset-0 transition-opacity">
              <div className="absolute inset-0 bg-black opacity-60"></div>
            </div>
          </TransitionChild>
          {/* This element is to trick the browser into centering the modal contents. */}
          <span className="inline-block align-middle h-screen"></span>
          &#8203;
          <TransitionChild
            as="div"
            className="fixed flex flex-col w-full text-left bg-gray-xlight border-b border-gray-light rounded-t-xl transform transition-all align-bottom bottom-[73px] left-0 overflow-y-scroll max-h-[calc(100vh-150px)]"
            role="dialog"
            aria-modal="true"
            aria-labelledby="modal-headline"
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-2 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-2 sm:translate-y-0 sm:scale-95"
          >
            <div className="sticky flex flex-col justify-between w-full h-full px-2 rounded-t-xl space-y-2 overflow-y-auto">
              <div className="flex flex-cols-2 w-full sticky py-2 top-0 border-b border-gray-light z-10 justify-center items-center">
                <div className="flex flex-col w-full items-start">
                  <ButtonIcon
                    variant="text"
                    color="gray"
                    size="sm"
                    className="items-center justify-center h-10 w-10"
                    isCircle={true}
                    icon={IconNames.X}
                    ariaDescribedby="close mobile quotes modal"
                    onClick={() => {
                      setDateRangePickerFocusedInput('startDate');
                      closeModal();
                    }}
                  />
                </div>

                <div className="flex flex-col w-full items-end text-right justify-center">
                  <Button onClick={onClearDatesClick} variant="text">
                    <Typography variant="subtitle2" className="text-black">
                      Clear Dates
                    </Typography>
                  </Button>
                </div>
              </div>

              <div className="flex flex-col w-full space-y-2 px-2 justify-between pt-3">
                <div className="flex w-full text-gray-dark px-2">
                  <Typography
                    component="span"
                    variant="body2"
                    variantM="body1"
                    className="font-semibold"
                  >
                    {dateRangeText()}
                  </Typography>
                </div>

                <div className="flex w-full text-gray-dark px-2 pb-2">
                  <Typography component="span" variant="body2" variantM="body2">
                    {dateRangeDetailsText()}
                  </Typography>
                </div>

                <div className="flex w-full justify-between space-x-2">
                  <Input
                    label="Check-in"
                    name="checkInDate"
                    data-testid={'@rates/check-in-date'}
                    value={
                      checkInDate
                        ? formatDateMMMD(moment(checkInDate))
                        : undefined
                    }
                    onFocus={() => {
                      setDateRangePickerFocusedInput('startDate');
                    }}
                    isInvalid={datesInvalid || false}
                    rightContent={
                      checkInDate && (
                        <ButtonIcon
                          variant="text"
                          color="gray"
                          size="sm"
                          icon={IconNames.CIRCLE_X}
                          iconSize="lg"
                          ariaDescribedby="clear check in date input"
                          onClick={() => {
                            setCheckInDate && setCheckInDate(null);
                            setDateRangePickerFocusedInput('startDate');
                          }}
                          className="ps-0 pe-0"
                        />
                      )
                    }
                    autoComplete="off"
                    hasCaret={false}
                  />

                  <Input
                    label="Check-out"
                    name="checkOutDate"
                    data-testid={'@rates/check-in-date'}
                    value={
                      checkOutDate
                        ? formatDateMMMD(moment(checkOutDate))
                        : undefined
                    }
                    onFocus={() => {
                      if (checkOutDate) {
                        setDateRangePickerFocusedInput('endDate');
                      } else {
                        setDateRangePickerFocusedInput('startDate');
                      }
                    }}
                    isInvalid={datesInvalid || false}
                    rightContent={
                      checkOutDate && (
                        <ButtonIcon
                          variant="text"
                          color="gray"
                          size="sm"
                          icon={IconNames.CIRCLE_X}
                          iconSize="lg"
                          ariaDescribedby="clear check out date input"
                          onClick={() => {
                            setCheckOutDate && setCheckOutDate(null);
                            setDateRangePickerFocusedInput('endDate');
                          }}
                          className="ps-0 pe-0"
                        />
                      )
                    }
                    autoComplete="off"
                    hasCaret={false}
                  />
                </div>
              </div>

              <div className="flex justify-center items-center w-full h-full py-4 overflow-hidden">
                <div className="flex w-auto h-full justify-center items-center border border-gray-light">
                  <DayPickerComponent
                    verticalBorderSpacing={0}
                    enableOutsideDays={false}
                    minDate={currentMoment}
                    noBorder={true}
                    numberOfMonths={1}
                    initialVisibleMonth={null}
                    focusedInput={dateRangePickerFocusedInput}
                    onDatesChange={onDatesChange}
                    onFocusChange={setDateRangePickerFocusedInput}
                    startDate={checkInDate ? moment(checkInDate) : null}
                    endDate={checkOutDate ? moment(checkOutDate) : null}
                    isDayBlocked={(day: moment.Moment) => isDayBlocked(day)}
                    isOutsideRange={(day: moment.Moment) =>
                      isStartDayBlocked(day) ||
                      (isEndDayBlocked(day) && !isDayBlocked(day))
                    }
                    hideKeyboardShortcutsPanel={true}
                    disabled={availabilityDataLoading}
                    minimumNights={minimumNightsForSelectedDate}
                    daysViolatingMinNightsCanBeClicked={false}
                    getMinNightsForHoverDate={getMinNightsForHoverDate}
                  />
                </div>
              </div>
            </div>

            {datesPresent && bookingQuotePresent && (
              <QuoteBreakdownInfo listing={listing} />
            )}
          </TransitionChild>
        </div>
      </Transition>,
      document.getElementById('modal-root') as Element
    );
  } else {
    return null;
  }
};
